// Android Health Connect (steps + active calories), read-only. Mirrors lib/healthkit.js.
// Health Connect aggregates Samsung Health, Google Fit, Fitbit, Garmin, Oura, Whoop… so one link covers most devices.
import { Health } from "@capgo/capacitor-health";
import { Capacitor } from "@capacitor/core";
import { isNativeApp, nativePlatform } from "@/lib/native";

const TYPES = ["steps", "calories"];

/** Android app build that includes the plugin. Older builds / web / iOS → false. */
export function isHealthConnectEnabled() {
  return isNativeApp() && nativePlatform() === "android" && Capacitor.isPluginAvailable("Health");
}

/** { available, reason } — reason "not_installed" style strings come from the plugin when HC is missing. */
export async function healthConnectStatus() {
  if (!isHealthConnectEnabled()) return { available: false, reason: "unsupported" };
  try { return await Health.isAvailable(); } catch (e) { return { available: false, reason: String(e?.message || e) }; }
}

export async function requestHealthConnectAccess() {
  const st = await healthConnectStatus();
  if (!st.available) return { ok: false, reason: st.reason };
  try {
    const res = await Health.requestAuthorization({ read: TYPES, write: [] });
    return { ok: (res?.readAuthorized || []).includes("steps"), calories: (res?.readAuthorized || []).includes("calories") };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}

export async function healthConnectGranted() {
  if (!isHealthConnectEnabled()) return [];
  try { return (await Health.checkAuthorization({ read: TYPES, write: [] }))?.readAuthorized || []; } catch { return []; }
}

const dayKey = (iso) => new Date(iso).toLocaleDateString("en-CA");

// Same multi-source dedupe as HealthKit: per day keep the single source with the biggest total
// (phone + watch both report the same walk; summing would double it).
function perDayMaxSource(samples) {
  const days = new Map();
  for (const s of samples || []) {
    if (!s?.startDate) continue;
    const k = dayKey(s.startDate);
    const src = s.sourceId || s.sourceName || "unknown";
    const m = days.get(k) || new Map();
    m.set(src, (m.get(src) || 0) + Number(s.value || 0));
    days.set(k, m);
  }
  const out = new Map();
  for (const [k, m] of days) out.set(k, Math.round(Math.max(0, ...m.values())));
  return out;
}

/** Last N local days (incl. today) → [{ date, steps, calories }]. */
export async function queryHealthConnectDays(n = 7) {
  if (!isHealthConnectEnabled()) return [];
  const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - (n - 1));
  const end = new Date(); end.setHours(24, 0, 0, 0);
  const read = async (dataType) => {
    try {
      const r = await Health.readSamples({ dataType, startDate: start.toISOString(), endDate: end.toISOString(), limit: 10000, ascending: true });
      return perDayMaxSource(r?.samples);
    } catch { return new Map(); }
  };
  const [steps, cals] = await Promise.all([read("steps"), read("calories")]);
  const rows = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    const k = d.toLocaleDateString("en-CA");
    rows.push({ date: k, steps: steps.get(k) || 0, calories: cals.get(k) || 0 });
  }
  return rows;
}

export function openHealthConnectInstall() {
  window.open("https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata", "_blank");
}
